import type { AnyID } from '@types'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { collideModularLinkedItems, type ModularCollideResult } from './collideModular'

export type ResnapSectionStore = {
  items: Map<string, { matrix?: Matrix4; type?: string; catalogID?: unknown }>
  collisionableItems: Set<AnyID>
  collideItems: (
    dragId: AnyID,
    anchorId: AnyID,
    opts?: { forcedSide?: 'left' | 'right' },
  ) => ModularCollideResult
  collidePouffItems: (
    dragId: AnyID,
    anchorId: AnyID,
    opts?: { forcedSide?: 'left' | 'right' },
  ) => ModularCollideResult
  updateSelectedObjectsPosition: (id: AnyID, matrix: Matrix4) => void
  setOBB: (id: AnyID) => void
}

const MAX_RESNAP_DISTANCE_MM = 2200

/**
 * Tras girar 90°, re-snapea probando vecinos ordenados por distancia.
 * Rinconeros 166- usan collideItems (caras L); módulos rectos usan OGadapted.
 */
export function resnapModularSectionAfterRotate(itemId: AnyID, store: ResnapSectionStore): void {
  const item = store.items.get(String(itemId))
  if (!item?.matrix || item.type !== 'linkedItem') return

  const pos = new Vector3()
  item.matrix.decompose(pos, new Quaternion(), new Vector3())

  const getCatalog = (id: AnyID) => store.items.get(String(id))?.catalogID

  const targets: Array<{ id: AnyID; dist: number }> = []
  store.collisionableItems.forEach((otherId) => {
    if (String(otherId) === String(itemId)) return
    const other = store.items.get(String(otherId))
    if (!other?.matrix || other.type !== 'linkedItem') return
    const otherPos = new Vector3()
    other.matrix.decompose(otherPos, new Quaternion(), new Vector3())
    const dist = Math.hypot(pos.x - otherPos.x, pos.z - otherPos.z)
    targets.push({ id: otherId, dist })
  })

  targets.sort((a, b) => a.dist - b.dist)

  for (const { id, dist } of targets) {
    if (dist > MAX_RESNAP_DISTANCE_MM) break

    const result = collideModularLinkedItems(
      itemId,
      id,
      getCatalog,
      store.collideItems,
      store.collidePouffItems,
      { forceReposition: true },
    )

    if (!result.snapped) continue

    const snapped = store.items.get(String(itemId))
    if (snapped?.matrix) {
      store.updateSelectedObjectsPosition(itemId, snapped.matrix.clone())
      store.setOBB(itemId)
    }
    return
  }
}
